import { type ActionType } from '@soulhx/fs-common';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { db, useAsyncLiveQuery, useSpecialActionTypes } from '@soulhx/client-store';

export interface GetActionTypesResponse {
  /**
   * List of action types, with i18n-translated display names
   */
  actionTypes: ActionType[] | undefined;

  /**
   * Indicator that the data is still loading
   */
  actionTypesLoading: boolean;
}

interface GetATOptions {
  /**
   * Whether to use **long** versions of the `displayName` (true) or
   * **short** (false); defaults to true/long
   */
  longNames?: boolean;

  /**
   * Whether to filter the results to bring back only available
   * types (true/default) or all results (false)
   */
  availableOnly?: boolean;

  /**
   * Whether the Scripture action type should be included (defaults
   * to false). This is a special "action type" (read-only), so
   * only useful in some circumstances.
   */
  includeScripture?: boolean;
}

/**
 * Hook to act as a wrapper around `useGetActionTypesQuery()` to
 * handle i18n and (optional) filtering.
 *
 * @returns Same results as if `useGetActionTypesQuery()` were
 * called, but with i18n displayNames and (optionally) filtered
 * to only show active
 */
export const useGetActionTypes = ({
  longNames = true,
  availableOnly = true,
  includeScripture = false,
}: GetATOptions = {}): GetActionTypesResponse => {
  const { t } = useTranslation();
  const { data: specialIds, isLoading: specialTypesLoading } = useSpecialActionTypes();

  const getDisplayName = useCallback(
    (type: ActionType): string => {
      if (specialTypesLoading) return type.displayName;

      const i18nPrefix = longNames ? 'do:actionTypes.long.' : 'do:actionTypes.short.';

      if (type.id === specialIds.book) {
        return t(i18nPrefix + 'book');
      }
      if (type.id === specialIds.church) {
        return t(i18nPrefix + 'church');
      }
      if (type.id === specialIds.journal) {
        return t(i18nPrefix + 'journal');
      }
      if (type.id === specialIds.longNT) {
        return t(i18nPrefix + 'longNT');
      }
      if (type.id === specialIds.longOT) {
        return t(i18nPrefix + 'longOT');
      }
      if (type.id === specialIds.podcast) {
        return t(i18nPrefix + 'podcast');
      }
      if (type.id === specialIds.prayer) {
        return t(i18nPrefix + 'prayer');
      }
      if (type.id === specialIds.scripture) {
        return t(i18nPrefix + 'scripture');
      }
      if (type.id === specialIds.shortNT) {
        return t(i18nPrefix + 'shortNT');
      }
      if (type.id === specialIds.shortOT) {
        return t(i18nPrefix + 'shortOT');
      }

      return type.displayName;
    },
    [specialIds, t, longNames, specialTypesLoading]
  );

  const { data, isLoading } = useAsyncLiveQuery<ActionType[]>(async () => {
    if (specialTypesLoading) return [];

    let dbTypes = await db.actionTypes.toArray();

    if (availableOnly) {
      dbTypes = dbTypes.filter((t) => t.isAvailable);
    }

    let theArray: ActionType[] = dbTypes.map((at) => ({
      id: at.id,
      displayName: getDisplayName(at),
      isAvailable: at.isAvailable,
      isDefault: at.isDefault,
    }));

    if (includeScripture) {
      const index = theArray.findIndex((at) => at.id === specialIds.scripture);
      if (index < 1) {
        theArray = theArray.concat({
          id: specialIds.scripture,
          displayName: longNames ? t('do:actionTypes.long.scripture') : t('do:actionTypes.short.scripture'),
          isAvailable: true,
          isDefault: true,
        });
      } else {
        theArray[index].isAvailable = true;
      }
    } else {
      theArray = theArray.filter((at) => at.id !== specialIds.scripture);
    }

    if (availableOnly) {
      return theArray.filter((at) => at.isAvailable === true);
    }

    return theArray;
  }, [specialIds, specialTypesLoading, includeScripture, longNames, availableOnly]);

  return { actionTypes: data ?? undefined, actionTypesLoading: isLoading };
};
